/**
 * IndexedDB primitives for the offline queue.
 * Owned stores: pending_inspections, pending_photos, pending_audio, meta.
 * All async APIs; nobody outside this file talks to indexedDB directly.
 */
const DB_NAME = "hag_offline";
const DB_VERSION = 3;
export const STORE_INSPECTIONS = "pending_inspections";
export const STORE_PHOTOS = "pending_photos";
export const STORE_AUDIO = "pending_audio";
export const STORE_META = "meta";

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_INSPECTIONS)) db.createObjectStore(STORE_INSPECTIONS, { keyPath: "id" });
      if (!db.objectStoreNames.contains(STORE_PHOTOS)) db.createObjectStore(STORE_PHOTOS, { keyPath: "id" });
      if (!db.objectStoreNames.contains(STORE_AUDIO)) db.createObjectStore(STORE_AUDIO, { keyPath: "id" });
      if (!db.objectStoreNames.contains(STORE_META)) db.createObjectStore(STORE_META, { keyPath: "key" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(storeName, mode = "readonly") {
  const db = await openDB();
  return db.transaction(storeName, mode).objectStore(storeName);
}

// ---- Inspections ----
export async function queueInspection(rec) {
  const store = await tx(STORE_INSPECTIONS, "readwrite");
  return new Promise((resolve, reject) => {
    const r = store.put(rec);
    r.onsuccess = () => resolve(rec);
    r.onerror = () => reject(r.error);
  });
}

export async function listPending() {
  const store = await tx(STORE_INSPECTIONS);
  return new Promise((resolve) => {
    const items = [];
    const req = store.openCursor();
    req.onsuccess = (e) => {
      const cur = e.target.result;
      if (cur) { items.push(cur.value); cur.continue(); } else resolve(items);
    };
  });
}

export async function removePending(id) {
  const store = await tx(STORE_INSPECTIONS, "readwrite");
  return new Promise((resolve) => { const r = store.delete(id); r.onsuccess = () => resolve(); });
}

export async function markPendingFailed(id, error) {
  const store = await tx(STORE_INSPECTIONS, "readwrite");
  return new Promise((resolve) => {
    const g = store.get(id);
    g.onsuccess = () => {
      const rec = g.result;
      if (!rec) return resolve();
      rec.attempts = (rec.attempts || 0) + 1;
      rec.last_error = error;
      rec.next_attempt_at = new Date(Date.now() + Math.min(60_000 * 2 ** rec.attempts, 30 * 60_000)).toISOString();
      store.put(rec).onsuccess = () => resolve();
    };
  });
}

// ---- Photos (blobs survive reload) ----
export async function queuePhoto(rec) {
  const store = await tx(STORE_PHOTOS, "readwrite");
  return new Promise((resolve, reject) => {
    const r = store.put(rec);
    r.onsuccess = () => resolve(rec);
    r.onerror = () => reject(r.error);
  });
}

export async function listQueuedPhotos(ids) {
  const store = await tx(STORE_PHOTOS);
  return new Promise((resolve) => {
    const items = [];
    const req = store.openCursor();
    req.onsuccess = (e) => {
      const cur = e.target.result;
      if (cur) {
        if (!ids || ids.includes(cur.value.id)) items.push(cur.value);
        cur.continue();
      } else resolve(items);
    };
  });
}

export async function removePhoto(id) {
  const store = await tx(STORE_PHOTOS, "readwrite");
  return new Promise((resolve) => { const r = store.delete(id); r.onsuccess = () => resolve(); });
}

// ---- Audio (voice notes) ----
export async function queueAudio(rec) {
  const store = await tx(STORE_AUDIO, "readwrite");
  return new Promise((resolve, reject) => {
    const r = store.put(rec);
    r.onsuccess = () => resolve(rec);
    r.onerror = () => reject(r.error);
  });
}

export async function getQueuedAudio(id) {
  const store = await tx(STORE_AUDIO);
  return new Promise((resolve) => {
    const r = store.get(id);
    r.onsuccess = () => resolve(r.result);
  });
}

export async function removeAudio(id) {
  const store = await tx(STORE_AUDIO, "readwrite");
  return new Promise((resolve) => { const r = store.delete(id); r.onsuccess = () => resolve(); });
}

// ---- Meta (last sync time, etc.) ----
export async function setMeta(key, value) {
  const store = await tx(STORE_META, "readwrite");
  return new Promise((resolve) => { const r = store.put({ key, value }); r.onsuccess = () => resolve(); });
}

export async function getMeta(key) {
  const store = await tx(STORE_META);
  return new Promise((resolve) => {
    const r = store.get(key);
    r.onsuccess = () => resolve(r.result?.value);
  });
}

export async function oldestPendingAt() {
  const items = await listPending();
  if (!items.length) return null;
  return items.reduce((a, b) => (a.queued_at < b.queued_at ? a : b)).queued_at;
}
